import { useEffect, useMemo, useState } from 'react';
import useTableFilters from '@/src/hooks/utils/useTableFilters';
import useMasterGameDefinitions from '@/src/hooks/data/useMasterGameDefinitions';
import useLobbySocket from './useLobbySocket';
import useLobbyConnectionStatus from '../data/useLobbyConnectionStatus';

export default function useRoomTables(tables, roomId) {
    const { connected } = useLobbyConnectionStatus();
    const gameDefinitions = useMasterGameDefinitions();
    const { sendRoomSubscriptionRequest, sendRoomDataRequest, sendRoomUnsubscriptionRequest } = useLobbySocket();
    const [isLoading, setIsLoading] = useState(true);

    // Initialize filter hook
    const {
        appliedFilters,
        toggleFilter,
        removeFilter,
        clearAllFilters,
        filterTableData,
        getAvailableFilters,
        isValidFilter,
        hasActiveFilters
    } = useTableFilters();

    useEffect(() => {
        try {
            if (roomId && connected) {
                setIsLoading(true);
                sendRoomDataRequest(roomId);
                sendRoomSubscriptionRequest(roomId);
            } else {
                console.error('No room ID found', roomId);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error in room subscription effect:', error);
            setIsLoading(false);
        }

        return () => {
            try {
                sendRoomUnsubscriptionRequest(roomId);
            } catch (error) {
                console.error('Error unsubscribing from room:', error);
            }
        }
    }, [connected, roomId, sendRoomDataRequest, sendRoomSubscriptionRequest, sendRoomUnsubscriptionRequest]);

    // Set loading to false when tables data becomes available
    useEffect(() => {
        if (tables !== undefined && tables !== null) {
            setIsLoading(false);
        }
    }, [tables]);

    // Convert tables object to array format for SortableTable with error handling
    const tableDataArray = useMemo(() => {
        try {
            if (!tables || typeof tables !== 'object') {
                console.warn('Invalid tables data:', tables);
                return [];
            }

            return Object.entries(tables).map(([tableId, tableData]) => {
                if (!tableId || !tableData || tableData.status?.toLowerCase() === 'inactive') {
                    console.warn('Invalid table entry:', { tableId, tableData });
                    return null;
                }
                return { tableId, tableData };
            }).filter(Boolean); // Remove null entries
        } catch (error) {
            console.error('Error processing tables data:', error);
            return [];
        }
    }, [tables]);

    // Filter the table data based on applied filters with error handling
    const filteredTableData = useMemo(() => {
        try {
            return filterTableData(tableDataArray, gameDefinitions);
        } catch (error) {
            console.error('Error filtering table data:', error);
            return tableDataArray; // Return original data on error
        }
    }, [tableDataArray, gameDefinitions, filterTableData]);

    return {
        tableDataArray,
        filteredTableData,
        appliedFilters,
        toggleFilter,
        removeFilter,
        clearAllFilters,
        getAvailableFilters,
        isValidFilter,
        hasActiveFilters,
        isLoading
    };
} 